import { useFormik, Form, FormikProvider } from 'formik';
import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'src/components/ui/toast';
import Tooltip from '@/components/ui/Tooltip';
import Button from 'src/components/ui/Button';
import {
  PaperPlaneRightIcon,
  PlusIcon,
  SquareIcon,
  XIcon,
  MicrophoneIcon,
  SpinnerGapIcon,
  CheckIcon,
  GearIcon,
  GlobeSimpleIcon,
  UserGearIcon,
  CameraIcon,
  UploadIcon,
  PaperclipIcon,
  CaretRightIcon,
  CaretLeftIcon,
  FunnelSimpleIcon,
  AtIcon
} from '@phosphor-icons/react';
import useWhisper from 'src/hooks/useWhisper';
import useAuth from 'src/hooks/useAuth';
import { useLocation, useSearchParams } from 'react-router-dom';
import Waveform from 'src/components/ui/Waveform';
import Popover from 'src/components/ui/Popover';
import { Menu, MenuItem } from '@/components/ui/Menu';
import { usePostHog } from 'src/lib/posthog';
import { DOC_HUB_NONE, DOC_HUB_NO_GROUPS } from '../hooks/useChatUrlState';
import ResourceTagInput, { ResourceTagInputHandle, EditorContentPart } from './ResourceTagInput';
import { FileInputPart, ChatInputPart } from '../hooks/useChatStream';
import {
  ChatSearchFilters,
  FilterType,
  FilterOption,
  Specialist,
  ParentPart,
  ImagePart,
  FilePart,
  TaggedResourcePart
} from '../types';
import { ChatRequest } from '../hooks/useChatStream';
import type { HomeChatHandoff, HomeChatHandoffResult } from '../pendingChatRequest';
import { useChatFileUpload, FileUpload, GEMINI_CONTEXT_LIMIT, CompletedUpload } from '../hooks/useChatFileUpload';
import { useDataDrivenFilterOptions } from '../hooks/useDataDrivenFilterOptions';
import { useFilterSearch } from '../hooks/useFilterSearch';
import { useFilterDocuments } from '../hooks/useFilterDocuments';
import { useAggregations } from 'src/features/database/hooks/useAggregations';
import { inferContentType } from 'src/features/uploads/utils';
import { useCategoryDefaults } from '../hooks/useCategoryDefaults';
import AllFiltersPanel from './AllFiltersPanel';
import SourcesPanel from './SourcesPanel';
import ImageModal from './ImageModal';
import FileItemPill from './FileItemPill';
import ImageItemPill from './ImageItemPill';
import { UploadProgressMap } from '../contexts/UploadProgressContext';
import { useSpecialist } from 'src/contexts/SpecialistContext';
import { useIsFreemiumUser } from 'src/hooks/useIsFreemiumUser';
import SpecialistPill from './SpecialistPill';
import PremiumUpsellModal from 'src/components/ui/PremiumUpsellModal';
import { patchChatSessionSetting } from '../api';
import { toTenantPath } from 'src/lib/tenantRouting';

export interface FollowUpSuggestion {
  prompt: string;
  /** The assistant message the suggestion came from. */
  messageId: string;
}

interface ChatSearchInputProps {
  sessionId?: string;
  handleResponse: (chatRequest: ChatRequest) => void;
  isRunning?: boolean;
  hasStreamStarted?: boolean;
  stopStreaming?: () => void;
  allowFilters?: boolean;
  chatSearchValue: string;
  /** Replaces the editor text and focuses the caret at the end. A new `id` applies the text again. */
  prefill?: { id: number; text: string } | null;
  /** Model-suggested next step shown as ghost text in the empty input. */
  followUpSuggestion?: FollowUpSuggestion;
  chatFilters?: ChatSearchFilters;
  setChatFilters?: (filters: ChatSearchFilters) => void;
  googleSearchPermission: boolean;
  setGoogleSearchPermission: (enabled: boolean) => void;
  enableGoogleSearch?: boolean;
  enableFileUpload?: boolean;
  enableSpecialist?: boolean;
  /** When false, the default category filters haven't loaded yet — submission is blocked to prevent sending empty filters. */
  filtersReady?: boolean;
  chatError?: string | null; // Error from chat stream (e.g., file too large)
  /** Add a user message to the chat immediately (before uploads finish). */
  addPendingUserMessage?: (parts: ParentPart[]) => void;
  /** Mark all processing file parts in the last user message as no longer processing. */
  clearPendingFileParts?: (
    documentIdsByUploadKey?: Map<string, string>,
    fallbackUrlsByUploadKey?: Map<string, string>
  ) => void;
  /** Set an error on the last user message (keeps message in history with error annotation). */
  setLastUserMessageError?: (error: string) => void;
  /** Called whenever the upload progress map changes (for threading to message rendering). */
  onUploadProgressChange?: (progressMap: UploadProgressMap) => void;
  /** True while the open canvas has an image insert still uploading or scanning; blocks send. */
  isCanvasScanning?: boolean;
  /** Extra classes for the text editor, e.g. a taller min height. */
  editorClassName?: string;
  /** Focus the editor when the composer mounts. */
  autoFocus?: boolean;
  /** When true, composer controls cannot be focused or submitted. */
  disabled?: boolean;
  /** Show a drop overlay on the composer as soon as files are dragged over the page. */
  showPageFileDropOverlay?: boolean;
  /**
   * Homepage send: leave immediately with a pending user bubble; resolve the
   * request after uploads finish so unmounting this composer does not stall UX.
   */
  onLeavePageSubmit?: (handoff: HomeChatHandoff) => void;
}

export default function ChatSearchInput({
  sessionId,
  handleResponse,
  isRunning,
  hasStreamStarted = false,
  stopStreaming,
  allowFilters,
  chatSearchValue,
  prefill,
  followUpSuggestion,
  chatFilters,
  setChatFilters,
  filtersReady = true,
  googleSearchPermission,
  setGoogleSearchPermission,
  enableGoogleSearch = true,
  enableFileUpload,
  enableSpecialist = true,
  chatError,
  addPendingUserMessage,
  clearPendingFileParts,
  setLastUserMessageError,
  onUploadProgressChange,
  isCanvasScanning = false,
  editorClassName,
  autoFocus = false,
  disabled = false,
  onLeavePageSubmit,
  showPageFileDropOverlay = false
}: ChatSearchInputProps) {
  const isFreemiumUser = useIsFreemiumUser();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const {
    selectedSpecialist,
    setSelectedSpecialist,
    specialists,
    specialistsLoading,
    fetchSpecialists,
    openManageModal,
    displayedSpecialists
  } = useSpecialist();

  // Premium upsell modal state for freemium users
  const [premiumModalOpen, setPremiumModalOpen] = useState<boolean>(false);
  const [premiumModalFeature, setPremiumModalFeature] = useState<string>('');

  // Whether the selected specialist has any knowledge-base restriction
  // configured — i.e. its knowledge_base type is not "default".
  // When true, the chat-level Sources button is disabled: the specialist
  // owns the scope and the user should edit the specialist (or deselect
  // it) to change it. Matches the production UX.
  const specialistHasFilters = useMemo(() => {
    if (!selectedSpecialist) return false;
    return selectedSpecialist.knowledge_base?.type !== 'default';
  }, [selectedSpecialist]);

  const activeTenant = user?.getActiveTenant?.(pathname);
  const isGoogleSearchEnabled = Boolean(enableGoogleSearch && activeTenant?.google_search_enabled);

  const showInlineSpecialistPill = enableSpecialist && !isFreemiumUser && !!selectedSpecialist;
  // A selected specialist with "Specific categories" knowledge base and web_access=true
  // pre-approves web search. Mirror that in the input so the user sees the
  // correct "Web Access" pill.
  const specialistForcesWebSearch = Boolean(
    selectedSpecialist?.knowledge_base?.type === 'specific_categories' && selectedSpecialist.knowledge_base.web_access
  );
  const effectiveWebSearchEnabled = googleSearchPermission || specialistForcesWebSearch;
  const showInlineWebSearch = isGoogleSearchEnabled && effectiveWebSearchEnabled;

  // Inline resource tag editor state
  const [editorHasContent, setEditorHasContent] = useState(false);

  const inputRef = useRef<ResourceTagInputHandle>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  // The follow-up the user accepted with Tab, until the input is cleared or sent.
  const acceptedFollowUpRef = useRef<FollowUpSuggestion | null>(null);
  const submittedStateRef = useRef<{
    query: string;
    editorHTML: string;
    uploads: Map<string, FileUpload>;
    followUp: FollowUpSuggestion | null;
  } | null>(null);
  const lastChatErrorRef = useRef<string | null>(null);
  const [mobileUploadPopoverOpen, setMobileUploadPopoverOpen] = useState<boolean>(false);
  const [mobileUploadPopoverAnchor, setMobileUploadPopoverAnchor] = useState<HTMLElement | null>(null);
  const [isWaitingForUploads, setIsWaitingForUploads] = useState(false);
  const uploadWaitAbortRef = useRef<AbortController | null>(null);

  // Eager file upload hook
  const fileUpload = useChatFileUpload();

  // Propagate upload progress to parent so message rendering can show live progress bars
  useEffect(() => {
    if (!onUploadProgressChange) return;
    const progressMap: UploadProgressMap = new Map();
    for (const upload of fileUpload.uploads.values()) {
      if (upload.status === 'uploading' || upload.status === 'scanning' || upload.status === 'processing') {
        progressMap.set(upload.file.name, { status: upload.status, uploadProgress: upload.uploadProgress });
      }
    }
    onUploadProgressChange(progressMap);
  }, [fileUpload.uploads, onUploadProgressChange]);

  // Cache for document tags - persists across tab switches for cascading logic
  const documentTagsCacheRef = useRef<Map<string, { category: string; domain: string; subdomain: string }>>(new Map());

  // Restore query and files only when the request failed before the stream
  // started. Once SSE has begun, the submitted query is already represented
  // in the conversation and restoring it would invite a duplicate submission.
  useEffect(() => {
    if (chatError && chatError !== lastChatErrorRef.current) {
      lastChatErrorRef.current = chatError;

      if (!hasStreamStarted) {
        restoreSubmittedState();
      } else {
        submittedStateRef.current = null;
      }
    }
  }, [chatError, hasStreamStarted]);

  // Clear saved submit data when stream completes successfully (no error)
  const prevIsRunningRef = useRef(false);
  useEffect(() => {
    if (prevIsRunningRef.current && !isRunning && !chatError) {
      submittedStateRef.current = null;
    }
    prevIsRunningRef.current = !!isRunning;
  }, [isRunning, chatError]);

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [filterModalType] = useState<FilterType>('domain');
  const [filterSearchQuery] = useState<string>('');
  const [debouncedDocSearch, setDebouncedDocSearch] = useState<string>('');

  // Track previous tab to detect tab switches
  const previousTabRef = useRef<FilterType>(filterModalType);

  // Store frozen options for the current tab during filter changes
  const frozenOptionsRef = useRef<{
    tabType: FilterType;
    options: FilterOption[];
    searchQuery: string;
  } | null>(null);
  const [specialistPopoverOpen, setSpecialistPopoverOpen] = useState<boolean>(false);
  const [specialistPopoverAnchor, setSpecialistPopoverAnchor] = useState<HTMLElement | null>(null);
  const [extrasMenuOpen, setExtrasMenuOpen] = useState<boolean>(false);
  const [extrasSubmenu, setExtrasSubmenu] = useState<'specialist' | null>(null);
  const extrasPlusAnchorRef = useRef<HTMLDivElement>(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState<boolean>(false);
  const [filterMenuShowFullFilters, setFilterMenuShowFullFilters] = useState<boolean>(false);
  const filterButtonAnchorRef = useRef<HTMLDivElement>(null);
  // One-shot flag to skip the next auto-deselect pass. Used when we know the
  // in-flight aggregations are stale (e.g. right after "Clear all filters"),
  // to avoid stripping valid selections based on the pre-clear filter counts.
  const skipNextAutoDeselectRef = useRef<boolean>(false);
  const [isUpdatingGoogleSearch, setIsUpdatingGoogleSearch] = useState<boolean>(false);
  const [imageModal, setImageModal] = useState<{
    open: boolean;
    url: string;
    filename: string;
    documentId?: string;
    fallbackUrl?: string;
  }>({
    open: false,
    url: '',
    filename: ''
  });
  const {
    isRecording: isMicRecording,
    isProcessing: isMicProcessing,
    transcript,
    audioHistory,
    startRecording,
    stopRecording,
    cancelRecording
  } = useWhisper();
  const posthog = usePostHog();

  // Memoize each filter array separately to prevent unnecessary recomputation.
  // chatFilters is derived from URL search params, so the arrays are recreated
  // on every render. Serialise to strings for value-based comparison.
  // IMPORTANT: Each filter must be memoized independently so that changing one
  // filter (e.g., document) doesn't create new references for others (e.g., category),
  // which would trigger re-fetches in useFilterDocuments.
  const categoryKey = chatFilters?.category?.join('|') ?? '';
  const domainKey = chatFilters?.domain?.join('|') ?? '';
  const subDomainKey = chatFilters?.subDomain?.join('|') ?? '';
  const documentKey = chatFilters?.document?.join('|') ?? '';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedCategory = useMemo(() => chatFilters?.category || [], [categoryKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedDomain = useMemo(() => chatFilters?.domain || [], [domainKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedSubDomain = useMemo(() => chatFilters?.subDomain || [], [subDomainKey]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedDocument = useMemo(() => chatFilters?.document || [], [documentKey]);

  const selectedFilters = useMemo(
    () => ({
      category: memoizedCategory,
      domain: memoizedDomain,
      subDomain: memoizedSubDomain,
      document: memoizedDocument
    }),
    [memoizedCategory, memoizedDomain, memoizedSubDomain, memoizedDocument]
  );

  const { data: categoryDefaults } = useCategoryDefaults();
  // All category names (enabled + disabled) for toggle discovery in SourcesPanel.
  const availableCategoryNames = categoryDefaults ? Object.keys(categoryDefaults) : null;

  // Only fetch counts when the full "All Wiki Filters" panel is open — that's
  // the only place counts are displayed. The Sources popover uses
  // defaultCategories (already fetched on page load) for toggle discovery.
  const { data: aggregations } = useAggregations({
    categories: memoizedCategory,
    domains: memoizedDomain,
    subdomains: memoizedSubDomain,
    documentIds: memoizedDocument,
    enabled: filtersReady && filterMenuOpen && filterMenuShowFullFilters
  });

  // Calculate badge count for Filters button.
  // Only reflects "advanced" filters (domain / subDomain / document); category
  // selections are intentionally excluded since they live in the main Popover
  // and are always set to some value by default.
  const filterBadgeCount = useMemo(() => {
    if (!chatFilters) return null;

    const advancedCount = chatFilters.domain.length + chatFilters.subDomain.length + chatFilters.document.length;

    return advancedCount > 0 ? advancedCount : null;
  }, [chatFilters]);

  // Clear frozen state when tab changes
  useEffect(() => {
    if (previousTabRef.current !== filterModalType) {
      frozenOptionsRef.current = null;
      previousTabRef.current = filterModalType;
    }
  }, [filterModalType]);

  // Debounce document search (300ms)
  useEffect(() => {
    if (filterModalType !== 'document') return;

    const timeout = setTimeout(() => {
      setDebouncedDocSearch(filterSearchQuery);
    }, 300);

    return () => clearTimeout(timeout);
  }, [filterSearchQuery, filterModalType]);

  // Only fetch documents when on the documents tab to avoid request spam
  // Cascading will work with whatever documents are loaded
  const filtersPanelOpen = Boolean(allowFilters && filterMenuOpen && filterMenuShowFullFilters);
  const isDocumentsTabActive = filtersPanelOpen && filterModalType === 'document';

  const filterDocsResult = useFilterDocuments({
    categories: selectedFilters.category,
    domains: selectedFilters.domain,
    subdomains: selectedFilters.subDomain,
    search: debouncedDocSearch,
    enabled: isDocumentsTabActive
  });

  // Populate document tags cache when documents are loaded
  useEffect(() => {
    filterDocsResult.documents.forEach((doc) => {
      documentTagsCacheRef.current.set(doc.id, doc.tags);
    });
  }, [filterDocsResult.documents]);

  // Derived filter data and options for the popup (data-driven counts/availability)
  const dataDriven = useDataDrivenFilterOptions(aggregations ?? undefined);
  const filteredOptionLabels = useFilterSearch(
    filterSearchQuery,
    filterModalType,
    (dataDriven.categories || []).map((o) => o.label),
    (dataDriven.domains || []).map((o) => o.label),
    (dataDriven.subDomains || []).map((o) => o.label),
    (dataDriven.documents || []).map((o) => o.label)
  );

  const getOptionCount = useCallback(
    (type: FilterType, option: string): number => {
      const list =
        type === 'category'
          ? dataDriven.categories
          : type === 'domain'
            ? dataDriven.domains
            : type === 'subDomain'
              ? dataDriven.subDomains
              : dataDriven.documents;
      const found = list.find((o) => o.value === option);
      return found?.count || 0;
    },
    [dataDriven]
  );

  const getAvailableItems = useCallback(
    (type: FilterType): string[] => {
      const list =
        type === 'category'
          ? dataDriven.categories
          : type === 'domain'
            ? dataDriven.domains
            : type === 'subDomain'
              ? dataDriven.subDomains
              : dataDriven.documents;
      return list.filter((o) => !o.disabled).map((o) => o.value);
    },
    [dataDriven]
  );

  const createFilterOptions = useCallback(
    (
      items: Array<{ label: string; value: string }>,
      type: FilterType,
      selectedValues: string[] = []
    ): FilterOption[] => {
      const availableItems = getAvailableItems(type);
      return items
        .map((option) => ({
          label: option.label,
          value: option.value,
          count: getOptionCount(type, option.value),
          disabled: !availableItems.includes(option.value)
        }))
        .sort((a, b) => {
          if (a.disabled !== b.disabled) {
            return a.disabled ? 1 : -1;
          }
          return a.label.localeCompare(b.label);
        });
    },
    [getAvailableItems, getOptionCount]
  );

  // Memoize document options separately to avoid unnecessary recalculations
  const documentOptions = useMemo(() => {
    return filterDocsResult.documents.map((doc) => ({
      label: doc.title,
      value: doc.id,
      count: 1,
      disabled: doc.disabled ?? false
    }));
  }, [filterDocsResult.documents]);

  // Memoize other filter options
  const otherFilterOptions = useMemo(() => {
    const optionsForType =
      filterModalType === 'category'
        ? dataDriven.categories
        : filterModalType === 'domain'
          ? dataDriven.domains
          : dataDriven.subDomains;

    const items = optionsForType
      .filter((o) => filteredOptionLabels.includes(o.label))
      .map((o) => ({ label: o.label, value: o.value }));
    return createFilterOptions(items, filterModalType, (chatFilters as any)?.[filterModalType] || []);
  }, [filteredOptionLabels, filterModalType, dataDriven, createFilterOptions, chatFilters]);

  const popupOptions = filterModalType === 'document' ? documentOptions : otherFilterOptions;

  // Options for display - use frozen if available for current tab and search hasn't changed.
  // Safe to read frozenOptionsRef here because it is only mutated in event handlers,
  // and every mutation also triggers a state change (setChatFilters) that updates
  // popupOptions — which is a useMemo dependency — forcing re-evaluation.
  // Auto-deselect empty filters on OTHER tabs when aggregations change.
  // Important: skip this while on the document tab so selecting one document
  // does not auto-prune category/domain/subDomain and block multi-bucket selection.
  useEffect(() => {
    if (!aggregations || !chatFilters || !setChatFilters) return;
    if (chatFilters.document.length > 0) return;

    // Consume the skip flag: we just cleared filters, so the aggregations that
    // triggered this run are based on the pre-clear state and must not be used
    // to prune the now-empty selections. The next run (with fresh aggregations)
    // will proceed normally.
    if (skipNextAutoDeselectRef.current) {
      skipNextAutoDeselectRef.current = false;
      return;
    }

    const currentTab = filterModalType;
    const updatedFilters = { ...chatFilters };
    let hasChanges = false;

    // Check each hierarchical filter type EXCEPT current tab
    const hierarchicalTypes: FilterType[] = ['category', 'domain', 'subDomain'];

    for (const type of hierarchicalTypes) {
      if (type === currentTab) continue; // Skip current tab - it stays frozen

      const selectedValues = chatFilters[type];
      if (selectedValues.length === 0) continue;

      const freshList =
        type === 'category' ? dataDriven.categories : type === 'domain' ? dataDriven.domains : dataDriven.subDomains;

      // Keep only values that are NOT disabled (have documents)
      const stillAvailable = selectedValues.filter((val) => {
        const option = freshList.find((o) => o.value === val);
        // Keep if option exists and is NOT disabled (has count > 0)
        return option && !option.disabled;
      });

      if (stillAvailable.length !== selectedValues.length) {
        updatedFilters[type] = stillAvailable;
        hasChanges = true;
      }
    }

    if (hasChanges) {
      setChatFilters(updatedFilters);
    }
    // Note: Intentionally exclude chatFilters/setChatFilters to prevent infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregations, dataDriven, filterModalType, chatFilters?.document?.length]);

  // Track when file uploads exceed context limit
  const prevExceededRef = useRef(false);
  useEffect(() => {
    if (fileUpload.exceedsContextLimit && !prevExceededRef.current) {
      posthog?.capture('chat_file_upload_too_large', {
        totalFileTokens: fileUpload.totalFileTokens,
        limit: GEMINI_CONTEXT_LIMIT
      });
    }
    prevExceededRef.current = fileUpload.exceedsContextLimit;
  }, [fileUpload.exceedsContextLimit, fileUpload.totalFileTokens, posthog]);

  const formik = useFormik({
    initialValues: {
      search: ''
    },
    onSubmit: async (_values, { setErrors, setSubmitting, resetForm }) => {
      if (disabled) return;
      try {
        const { parts: editorParts, hasText } = inputRef.current?.getContent() ?? {
          parts: [] as EditorContentPart[],
          hasText: false
        };

        // Manual validation: need text/resources or files
        if (!hasText && editorParts.every((p) => p.type !== 'resource') && fileUpload.files.length === 0) {
          return;
        }

        const query = editorParts
          .filter((p) => p.type === 'text')
          .map((p) => (p as any).text)
          .join(' ');
        const acceptedFollowUp = acceptedFollowUpRef.current;
        const followUpEdited = !!acceptedFollowUp && query.trim() !== acceptedFollowUp.prompt;
        const queryEvent = {
          query,
          files: Array.from(fileUpload.uploads.values()).map((u) => ({
            name: u.file.name,
            tokens: u.tokenCount
          })),
          from_follow_up: !!acceptedFollowUp,
          follow_up_edited: followUpEdited
        };

        // Block submission if files exceed context limit
        if (fileUpload.exceedsContextLimit) {
          posthog?.capture('chat_query_submitted', { ...queryEvent, exceedsContextLimit: true });
          toast.error('Query exceeds CompliMind chat capacity. Please remove some files.');
          return;
        }

        // Block submission if there are upload errors
        if (fileUpload.hasUploadErrors) {
          toast.error('Some file uploads failed. Please remove them and try again.');
          posthog?.capture('chat_query_submitted', { ...queryEvent, hasUploadErrors: true });
          return;
        }

        // Block submission while a canvas image is still scanning
        if (isCanvasScanning) {
          toast.error('Wait for the image to finish scanning, or cancel it, before sending.');
          return;
        }

        // Track chat query event
        posthog?.capture('chat_query_submitted', queryEvent);

        // Capture files and editor state before clearing
        const uploadsSnapshot = new Map(fileUpload.uploads);
        const filesToSend = Array.from(uploadsSnapshot.values()).map((u) => u.file);
        const hasUploads = uploadsSnapshot.size > 0;
        // Store state before clearing (for error restore)
        submittedStateRef.current = {
          query: editorParts
            .filter((p) => p.type === 'text')
            .map((p) => (p as any).text)
            .join(''),
          editorHTML: inputRef.current?.getHTML() ?? '',
          uploads: uploadsSnapshot,
          followUp: acceptedFollowUp
        };

        // Clear input and files immediately for better UX
        resetForm();
        inputRef.current?.clear();

        const buildPendingParts = (): ParentPart[] => {
          const pendingParts: ParentPart[] = [];
          let pendingTextBuffer = '';
          const flushPendingText = () => {
            if (pendingTextBuffer) {
              pendingParts.push({ type: 'basic', content: pendingTextBuffer });
              pendingTextBuffer = '';
            }
          };
          for (const ep of editorParts) {
            if (ep.type === 'text') {
              pendingTextBuffer += ep.text;
            } else if (ep.type === 'resource') {
              flushPendingText();
              pendingParts.push({
                type: 'tagged_resource',
                ref_id: 0,
                resource_type: ep.resource_type ?? 'document',
                title: ep.title ?? ep.resource_id,
                resource_id: ep.resource_id,
                content: ''
              } as TaggedResourcePart);
            }
          }
          flushPendingText();

          uploadsSnapshot.forEach((upload, uploadKey) => {
            const file = upload.file;
            const blobUrl = upload.previewUrl || URL.createObjectURL(file);
            const isStillProcessing = upload.status !== 'complete';
            const hydratedId = upload.status === 'complete' && upload.documentId ? upload.documentId : undefined;
            if (isImageFile(file)) {
              pendingParts.push({
                type: 'image',
                filename: file.name,
                url: blobUrl,
                fallback_url: upload.fallbackPreviewUrl,
                processing: isStillProcessing,
                document_id: hydratedId,
                uploadKey,
                content: ''
              } as ImagePart);
            } else {
              pendingParts.push({
                type: 'file',
                id: hydratedId,
                filename: file.name,
                url: blobUrl,
                processing: isStillProcessing,
                uploadKey,
                content: ''
              } as FilePart);
            }
          });
          return pendingParts;
        };

        const buildResolvedRequest = async (): Promise<HomeChatHandoffResult> => {
          let completedUploads: CompletedUpload[] = [];
          if (hasUploads) {
            completedUploads = await fileUpload.waitForUploads();
          }
          const latestUploads = fileUpload.getUploads();
          const postUploadTokens = Array.from(latestUploads.values()).reduce((sum, u) => sum + (u.tokenCount ?? 0), 0);
          if (postUploadTokens > GEMINI_CONTEXT_LIMIT) {
            fileUpload.clearUploads({ revokePreviewUrls: false });
            throw new Error('Query exceeds CompliMind chat capacity. Please remove some files.');
          }
          const hasPostUploadErrors = Array.from(latestUploads.values()).some((u) => u.status === 'error');
          if (hasPostUploadErrors) {
            fileUpload.clearUploads({ revokePreviewUrls: false });
            throw new Error('Some file uploads failed. Please remove them and try again.');
          }
          const documentIdsByUploadKey = new Map<string, string>();
          const fallbackUrlsByUploadKey = new Map<string, string>();
          for (const upload of completedUploads) {
            if (upload.document_id) {
              documentIdsByUploadKey.set(upload.upload_key, upload.document_id);
            }
            if (upload.preview_url) {
              fallbackUrlsByUploadKey.set(upload.upload_key, upload.preview_url);
            }
          }
          fileUpload.clearUploads({ revokePreviewUrls: false });

          const chatParts: ChatInputPart[] = [...editorParts];
          const seenFileIds = new Set<string>();
          completedUploads.forEach((u) => {
            if (seenFileIds.has(u.file_id)) return;
            seenFileIds.add(u.file_id);
            chatParts.push({ type: 'file', file_id: u.file_id, filename: u.filename } as FileInputPart);
          });

          const effectiveGoogleSearch = specialistForcesWebSearch || googleSearchPermission;
          let chatRequest: ChatRequest;
          if (!allowFilters) {
            chatRequest = {
              parts: chatParts,
              specialist_id: selectedSpecialist?.id,
              google_search: effectiveGoogleSearch
            };
          } else {
            const docHubParam = searchParams.get('docHubSource');
            const docHubSourcesForRequest =
              docHubParam == null
                ? undefined
                : docHubParam === DOC_HUB_NONE || docHubParam === ''
                  ? []
                  : chatFilters?.docHubSource?.filter((s) => s !== DOC_HUB_NO_GROUPS);
            const categoryParam = searchParams.get('category');
            const categoryFilterForRequest =
              categoryParam == null
                ? chatFilters?.category
                : categoryParam === DOC_HUB_NONE || categoryParam === ''
                  ? []
                  : chatFilters?.category;
            chatRequest = {
              parts: chatParts,
              category_filter: categoryFilterForRequest,
              doc_hub_sources: docHubSourcesForRequest,
              document_filter: chatFilters?.document,
              domain_filter: chatFilters?.domain,
              reference_filter: chatFilters?.subDomain,
              specialist_id: selectedSpecialist?.id,
              google_search: effectiveGoogleSearch
            };
          }
          return { request: chatRequest, documentIdsByUploadKey, fallbackUrlsByUploadKey };
        };

        if (onLeavePageSubmit) {
          fileUpload.clearUploads({ revokePreviewUrls: false, keepInflight: true });
          onLeavePageSubmit({
            pendingParts: buildPendingParts(),
            resolveRequest: buildResolvedRequest
          });
          return;
        }

        // Show user message in chat immediately, with processing spinners on pending file uploads
        const usedPendingMessage = !!(addPendingUserMessage && filesToSend.length > 0);
        if (usedPendingMessage) {
          addPendingUserMessage(buildPendingParts());
        }

        // Clear the input UI but keep in-flight uploads alive so progress continues
        fileUpload.clearUploads({ revokePreviewUrls: !usedPendingMessage, keepInflight: true });

        // Wait for any in-progress uploads to complete before sending.
        // ``waitForUploads`` reads from a synchronously-updated ref, so
        // the document_id for files that finished during the wait is
        // available immediately — unlike ``getUploads()`` below, whose
        // ``uploadsRef`` lags behind by a microtask because React's state
        // update queue runs after the upload promise resolves.
        let completedUploads: CompletedUpload[] = [];
        if (hasUploads) {
          const abortController = new AbortController();
          uploadWaitAbortRef.current = abortController;
          setIsWaitingForUploads(true);

          try {
            completedUploads = await new Promise<CompletedUpload[]>((resolve, reject) => {
              // If abort fires, reject so we can cancel gracefully
              abortController.signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')));
              fileUpload.waitForUploads().then(resolve, reject);
            });
          } finally {
            setIsWaitingForUploads(false);
            uploadWaitAbortRef.current = null;
          }
        }

        // Update the snapshot with final upload state (token counts are now available)
        // Use getUploads() to read latest state — fileUpload.uploads is stale in this async closure
        const latestUploads = fileUpload.getUploads();
        if (submittedStateRef.current) {
          submittedStateRef.current.uploads = new Map(latestUploads);
        }

        // Re-check token limit now that uploads have completed and token counts are known
        const postUploadTokens = Array.from(latestUploads.values()).reduce((sum, u) => sum + (u.tokenCount ?? 0), 0);
        if (postUploadTokens > GEMINI_CONTEXT_LIMIT) {
          clearPendingFileParts?.();
          setLastUserMessageError?.('Query exceeds CompliMind chat capacity. Please remove some files.');
          fileUpload.clearUploads({ revokePreviewUrls: !usedPendingMessage });
          restoreSubmittedState();
          toast.error('Query exceeds CompliMind chat capacity. Please remove some files.');
          return;
        }

        // Check for upload errors that occurred during waitForUploads
        const hasPostUploadErrors = Array.from(latestUploads.values()).some((u) => u.status === 'error');
        if (hasPostUploadErrors) {
          clearPendingFileParts?.();
          setLastUserMessageError?.('Some file uploads failed. Please remove them and try again.');
          fileUpload.clearUploads({ revokePreviewUrls: !usedPendingMessage });
          restoreSubmittedState();
          toast.error('Some file uploads failed. Please remove them and try again.');
          return;
        }

        // Build an upload-key → document ULID map so the pending user
        // message's file parts can be hydrated with real IDs. Without
        // this, completed uploads render as "no access" until a page
        // refresh, because the message stream never echoes the user's
        // own file parts back with the document ID attached. Keying by the
        // per-upload id (not filename) keeps two same-named files distinct.
        //
        // Source: ``completedUploads`` (synchronously-updated ref) rather
        // than ``latestUploads`` (React state) — the React state lags one
        // microtask behind the upload promise resolution, so for files
        // that finish during ``waitForUploads`` the state map still shows
        // them as ``processing`` with no document_id when we read it here.
        const documentIdsByUploadKey = new Map<string, string>();
        // A HEIC's browser-renderable rendition SAS, resolved only once the
        // upload registers — wired onto the pending image part so a message
        // sent before processing finished previews without needing a reload.
        const fallbackUrlsByUploadKey = new Map<string, string>();
        for (const upload of completedUploads) {
          if (upload.document_id) {
            documentIdsByUploadKey.set(upload.upload_key, upload.document_id);
          }
          if (upload.preview_url) {
            fallbackUrlsByUploadKey.set(upload.upload_key, upload.preview_url);
          }
        }

        fileUpload.clearUploads({ revokePreviewUrls: !usedPendingMessage });

        // Mark file parts as no longer processing now that uploads are done
        if (clearPendingFileParts) {
          clearPendingFileParts(documentIdsByUploadKey, fallbackUrlsByUploadKey);
        }

        // Build the ordered parts list:
        //   1. Text + resource parts from the editor (in their typed order)
        //   2. File parts — referenced by ULID after pre-upload completes
        const chatParts: ChatInputPart[] = [...editorParts];

        const seenFileIds = new Set<string>();
        completedUploads.forEach((u) => {
          if (seenFileIds.has(u.file_id)) return;
          seenFileIds.add(u.file_id);
          chatParts.push({ type: 'file', file_id: u.file_id, filename: u.filename } as FileInputPart);
        });

        // A selected specialist with web_access enabled pre-approves web search;
        // that wins over the session-level toggle.
        const effectiveGoogleSearch = specialistForcesWebSearch || googleSearchPermission;

        let chatRequest: ChatRequest;
        if (!allowFilters) {
          chatRequest = {
            parts: chatParts,
            specialist_id: selectedSpecialist?.id,
            google_search: effectiveGoogleSearch
          };
        } else {
          const docHubParam = searchParams.get('docHubSource');
          const docHubSourcesForRequest =
            docHubParam == null
              ? undefined
              : docHubParam === DOC_HUB_NONE || docHubParam === ''
                ? []
                : chatFilters?.docHubSource?.filter((s) => s !== DOC_HUB_NO_GROUPS);
          const categoryParam = searchParams.get('category');
          const categoryFilterForRequest =
            categoryParam == null
              ? chatFilters?.category
              : categoryParam === DOC_HUB_NONE || categoryParam === ''
                ? []
                : chatFilters?.category;
          chatRequest = {
            parts: chatParts,
            category_filter: categoryFilterForRequest,
            doc_hub_sources: docHubSourcesForRequest,
            document_filter: chatFilters?.document,
            domain_filter: chatFilters?.domain,
            reference_filter: chatFilters?.subDomain,
            specialist_id: selectedSpecialist?.id,
            google_search: effectiveGoogleSearch
          };
        }

        // Submit the request (handleResponse doesn't throw, errors are in state)
        handleResponse(chatRequest);
        if (acceptedFollowUp) {
          posthog?.capture('chat_follow_up_sent', {
            message_id: acceptedFollowUp.messageId,
            session_id: sessionId,
            follow_up: acceptedFollowUp.prompt,
            edited: followUpEdited
          });
        }

        // Don't clear submittedStateRef here — it will be cleared when the stream
        // completes successfully (isRunning → false without error) or consumed on error
      } catch (error: any) {
        // If the user cancelled during upload wait, clean up gracefully
        if (error instanceof DOMException && error.name === 'AbortError') {
          // Don't cancel uploads — let them continue in the background
          // Clear processing spinners on the message's file parts
          clearPendingFileParts?.();
          setLastUserMessageError?.('Cancelled');
          // Restore query and files to input (uploads still in progress will show spinners)
          restoreSubmittedState();
          return;
        }
        console.error(error);
        // Restore files and query on any error
        restoreSubmittedState();
        setSubmitting(false);
        setErrors({});
      }
    }
  });

  const { handleSubmit } = formik;

  // Shared helper: restore submitted content and files to the editor on error.
  const restoreSubmittedState = useCallback(() => {
    if (!submittedStateRef.current) return;
    const { editorHTML: savedHTML, uploads: savedUploads, followUp: savedFollowUp } = submittedStateRef.current;
    submittedStateRef.current = null;

    // Merge saved uploads with any the user added in the meantime.
    const mergedUploads = new Map(savedUploads);
    fileUpload.getUploads().forEach((upload, key) => mergedUploads.set(key, upload));
    fileUpload.restoreUploads(mergedUploads);

    // Restore editor content (preserving chips + order)
    if (inputRef.current) {
      const currentHTML = inputRef.current.getHTML();
      const merged = currentHTML && savedHTML ? savedHTML + '<br>' + currentHTML : savedHTML || currentHTML;
      inputRef.current.setHTML(merged);
    }
    // Keep follow-up attribution so a resend still counts as using it.
    acceptedFollowUpRef.current = savedFollowUp;
  }, [fileUpload]);

  useEffect(() => {
    if (chatSearchValue) inputRef.current?.setPlainText(chatSearchValue);
  }, [chatSearchValue]);

  useEffect(() => {
    if (!autoFocus) return;
    const frame = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [autoFocus]);

  useEffect(() => {
    if (!prefill) return;
    // A normal trailing space is collapsed by the contenteditable, so the caret
    // sits on the last word. A non-breaking space stays visible until the user types.
    inputRef.current?.setPlainText(`${prefill.text.trimEnd()}\u00A0`);
    const frame = requestAnimationFrame(() => inputRef.current?.focusAtEnd());
    return () => cancelAnimationFrame(frame);
  }, [prefill]);

  useEffect(() => {
    if (transcript) {
      posthog?.capture('chat_voice_input_transcribed', { transcript });
      inputRef.current?.appendText(transcript);
    }
  }, [transcript, posthog]);

  const SUPPORTED_TYPES = {
    '.pdf': 'application/pdf',
    '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.csv': 'text/csv',
    '.doc': 'application/msword',
    '.ppt': 'application/vnd.ms-powerpoint',
    '.html': 'text/html',
    '.txt': 'text/plain',
    '.odt': 'application/vnd.oasis.opendocument.text',
    '.odp': 'application/vnd.oasis.opendocument.presentation',
    '.htm': 'text/html',
    '.page': 'application/vnd.apple.pages',
    '.rtf': 'application/rtf',
    '.markdown': 'text/markdown',
    '.md': 'text/markdown',
    '.eml': 'message/rfc822',
    '.msg': 'application/vnd.ms-outlook',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
    '.heic': 'image/heic',
    '.heif': 'image/heif'
  };

  // Shared logic for adding files from any source (file input, drag-drop, paste).
  // Validates, deduplicates, and triggers eager upload (which also creates preview URLs).
  const addFiles = (newFiles: File[], { filterTypes = true }: { filterTypes?: boolean } = {}) => {
    // Match by resolved content type, not the browser's `file.type`, which is
    // empty for HEIC on non-Apple browsers. inferContentType resolves by extension.
    const supportedMimes = new Set(Object.values(SUPPORTED_TYPES));
    const validFiles = filterTypes
      ? newFiles.filter((file: File) => supportedMimes.has(file.type) || supportedMimes.has(inferContentType(file)))
      : newFiles;

    if (fileUpload.files.length + validFiles.length > 5) {
      toast.warning('You can only upload up to 5 files');
      return;
    }

    const uniqueFiles = validFiles.filter((newFile) => {
      // iPhone camera captures can reuse the same filename (e.g. image.jpg),
      // so using filename-only dedupe incorrectly drops distinct photos.
      return !fileUpload.files.some(
        (existingFile) =>
          existingFile.name === newFile.name &&
          existingFile.size === newFile.size &&
          existingFile.lastModified === newFile.lastModified &&
          existingFile.type === newFile.type
      );
    });

    if (uniqueFiles.length < validFiles.length) {
      toast.info('Some files were skipped because they were already added');
    }

    uniqueFiles.forEach((file) => {
      fileUpload.uploadFile(file);
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(event.target.files || []);

    // Reset the file input value so that the same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = '';
    }

    addFiles(newFiles);
  };

  const isImageFile = (file: File) => {
    // Resolve by extension, not the browser's `file.type`, which is empty for
    // HEIC on non-Apple browsers — otherwise the composer renders such a file
    // as a document chip instead of an image preview.
    return inferContentType(file).startsWith('image/');
  };

  const getFilePreviewUrl = (file: File) => {
    const entry = Array.from(fileUpload.uploads.values()).find((u) => u.file === file);
    return entry?.previewUrl || '';
  };

  const dragDepthRef = useRef(0);
  const addFilesRef = useRef(addFiles);
  addFilesRef.current = addFiles;

  const dragHasFiles = (transfer: DataTransfer | null) => !!transfer && Array.from(transfer.types).includes('Files');

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!enableFileUpload || disabled || !dragHasFiles(e.dataTransfer)) return;
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (showPageFileDropOverlay) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (!enableFileUpload || disabled) return;
    addFiles(Array.from(e.dataTransfer.files));
  };

  useEffect(() => {
    if (!showPageFileDropOverlay || !enableFileUpload || disabled) return;

    let pageDepth = 0;
    const onEnter = (event: DragEvent) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      pageDepth += 1;
      setIsDragging(true);
    };
    const onOver = (event: DragEvent) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      event.preventDefault();
    };
    const onLeave = (event: DragEvent) => {
      if (!dragHasFiles(event.dataTransfer)) return;
      pageDepth -= 1;
      if (pageDepth <= 0) {
        pageDepth = 0;
        dragDepthRef.current = 0;
        setIsDragging(false);
      }
    };
    const onDropOrEnd = (event: DragEvent) => {
      if (event.type === 'drop' && dragHasFiles(event.dataTransfer)) {
        event.preventDefault();
        if (event.dataTransfer?.files.length) {
          addFilesRef.current(Array.from(event.dataTransfer.files));
        }
      }
      pageDepth = 0;
      dragDepthRef.current = 0;
      setIsDragging(false);
    };

    document.addEventListener('dragenter', onEnter);
    document.addEventListener('dragover', onOver);
    document.addEventListener('dragleave', onLeave);
    window.addEventListener('drop', onDropOrEnd);
    window.addEventListener('dragend', onDropOrEnd);
    return () => {
      document.removeEventListener('dragenter', onEnter);
      document.removeEventListener('dragover', onOver);
      document.removeEventListener('dragleave', onLeave);
      window.removeEventListener('drop', onDropOrEnd);
      window.removeEventListener('dragend', onDropOrEnd);
    };
  }, [showPageFileDropOverlay, enableFileUpload, disabled]);

  const handleFilterSelectionChange = (type: FilterType, newSelected: string[]) => {
    if (!setChatFilters || !chatFilters) return;

    // Check if we're on the same tab (have frozen options for this tab type)
    const isOnSameTab = frozenOptionsRef.current?.tabType === type;

    // FREEZE: Store current tab's options before the first selection on this tab
    // Only freeze if we don't already have frozen options for this tab
    if (!isOnSameTab) {
      frozenOptionsRef.current = {
        tabType: type,
        options: [...popupOptions], // Clone current options
        searchQuery: filterSearchQuery
      };
    }

    // GUARD: Only validate against fresh aggregations when SWITCHING tabs
    // When on the same tab, trust the selection (user sees frozen options, so their clicks are valid)
    let validatedSelection = newSelected;

    if (type !== 'document' && !isOnSameTab) {
      const freshList =
        type === 'category' ? dataDriven.categories : type === 'domain' ? dataDriven.domains : dataDriven.subDomains;

      validatedSelection = newSelected.filter((val) => {
        const option = freshList.find((o) => o.value === val);
        return option && !option.disabled;
      });
    }

    // For document type, only update document selection.
    // Keep hierarchical filters unchanged so users can select documents
    // across multiple categories/domains/subdomains.
    if (type === 'document') {
      setChatFilters({
        ...chatFilters,
        document: newSelected
      });

      posthog?.capture('chat_filter_changed', { filter: type, value: newSelected });
      return;
    }

    // For category/domain/subDomain: update only the changed filter and cascade to documents
    // Do NOT cascade to other hierarchical filters - that's handled by auto-deselect effect
    const newCategorySelection = type === 'category' ? validatedSelection : chatFilters.category;
    const newDomainSelection = type === 'domain' ? validatedSelection : chatFilters.domain;
    const newSubDomainSelection = type === 'subDomain' ? validatedSelection : chatFilters.subDomain;

    // Only cascade to documents - filter out documents that don't match the new hierarchical selections
    let newDocumentSelection = chatFilters.document;

    if (newDocumentSelection.length > 0) {
      newDocumentSelection = newDocumentSelection.filter((docId) => {
        const tags = documentTagsCacheRef.current.get(docId);
        if (!tags) return true; // Keep if we don't have tag info (defensive)

        const matchesCategory = newCategorySelection.length === 0 || newCategorySelection.includes(tags.category);
        const matchesDomain = newDomainSelection.length === 0 || newDomainSelection.includes(tags.domain);
        const matchesSubDomain = newSubDomainSelection.length === 0 || newSubDomainSelection.includes(tags.subdomain);

        return matchesCategory && matchesDomain && matchesSubDomain;
      });
    }

    setChatFilters({
      ...chatFilters,
      category: newCategorySelection,
      domain: newDomainSelection,
      subDomain: newSubDomainSelection,
      document: newDocumentSelection
    });

    posthog?.capture('chat_filter_changed', { filter: type, value: validatedSelection });
  };

  // Specialist handlers
  const openSpecialistPopup = (anchorEl: HTMLElement) => {
    setSpecialistPopoverAnchor(anchorEl);
    setSpecialistPopoverOpen(true);
    // Only fetch if specialists haven't been loaded yet to avoid
    // triggering a loading state that causes the welcome message to refresh
    if (specialists.length === 0 && !specialistsLoading) {
      fetchSpecialists();
    }
  };

  const closeSpecialistPopup = () => {
    setSpecialistPopoverOpen(false);
  };

  const handlePremiumFeatureClick = (featureDescription: string) => {
    setPremiumModalFeature(featureDescription);
    setPremiumModalOpen(true);
    posthog?.capture('freemium_premium_feature_clicked', { feature: featureDescription });
  };

  const handleSelectSpecialist = (specialist: Specialist | null) => {
    setSelectedSpecialist(specialist); // This now updates URL via context
    if (specialist) {
      posthog?.capture('chat_specialist_selected', { specialistId: specialist.id, specialistName: specialist.name });
    } else {
      posthog?.capture('chat_specialist_cleared');
    }
    closeSpecialistPopup();
    setExtrasMenuOpen(false);
    setExtrasSubmenu(null);
  };

  const dismissWebSearch = useCallback(async () => {
    if (!googleSearchPermission || isUpdatingGoogleSearch) return;
    setGoogleSearchPermission(false);

    if (!sessionId) {
      posthog?.capture('chat_google_search_permission_toggled', {
        enabled: false,
        persisted: false
      });
      return;
    }

    setIsUpdatingGoogleSearch(true);
    try {
      await patchChatSessionSetting(sessionId, 'google_search', false);
      posthog?.capture('chat_google_search_permission_toggled', {
        enabled: false,
        persisted: true
      });
    } catch {
      setGoogleSearchPermission(true);
      toast.error('Failed to update Web Access for this conversation.');
    } finally {
      setIsUpdatingGoogleSearch(false);
    }
  }, [googleSearchPermission, isUpdatingGoogleSearch, posthog, sessionId, setGoogleSearchPermission]);

  const handleToggleGoogleSearch = useCallback(async () => {
    const nextValue = !googleSearchPermission;
    setGoogleSearchPermission(nextValue);

    if (!sessionId) {
      posthog?.capture('chat_google_search_permission_toggled', {
        enabled: nextValue,
        persisted: false
      });
      return;
    }

    setIsUpdatingGoogleSearch(true);
    try {
      await patchChatSessionSetting(sessionId, 'google_search', nextValue);
      posthog?.capture('chat_google_search_permission_toggled', {
        enabled: nextValue,
        persisted: true
      });
    } catch {
      setGoogleSearchPermission(!nextValue);
      toast.error('Failed to update Web Search for this conversation.');
    } finally {
      setIsUpdatingGoogleSearch(false);
    }
  }, [googleSearchPermission, posthog, sessionId, setGoogleSearchPermission]);

  const closeMobileUploadPopup = () => {
    setMobileUploadPopoverOpen(false);
  };

  const handleTakePhoto = () => {
    cameraInputRef.current?.click();
    closeMobileUploadPopup();
    posthog?.capture('chat_mobile_camera_opened');
  };

  const handleUploadFile = () => {
    fileInputRef.current?.click();
    closeMobileUploadPopup();
    posthog?.capture('chat_mobile_file_upload_opened');
  };

  const toggleSpecialistSubmenu = () => {
    setExtrasSubmenu((s) => {
      if (s === 'specialist') return null;
      if (specialists.length === 0 && !specialistsLoading) {
        fetchSpecialists();
      }
      return 'specialist';
    });
  };

  const handleAddResourceFromMenu = () => {
    setExtrasMenuOpen(false);
    setExtrasSubmenu(null);
    // Small delay so the popover finishes closing before the search popup opens
    setTimeout(() => inputRef.current?.openResourceSearch(), 50);
  };

  const handleAddFilesFromMenu = () => {
    setExtrasMenuOpen(false);
    setExtrasSubmenu(null);
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches) {
      setMobileUploadPopoverAnchor(extrasPlusAnchorRef.current);
      setMobileUploadPopoverOpen(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`w-full flex flex-col`}>
      <FormikProvider value={formik}>
        <Form
          autoComplete="off"
          noValidate
          onSubmit={(event) => {
            if (disabled) {
              event.preventDefault();
              return;
            }
            handleSubmit(event);
          }}
        >
          <div
            className={`relative border-[4px] border-border-default pt-2 p-1 md:p-3 bg-white flex flex-col rounded-2xl md:rounded-3xl ${
              isDragging ? 'border-primary-blue' : ''
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {enableFileUpload && isDragging && (
              <div
                className="absolute inset-0 z-20 flex flex-col items-center justify-center rounded-[1.1rem] md:rounded-[1.35rem] bg-white/80 pointer-events-none"
                aria-hidden
              >
                <UploadIcon size={36} weight="light" />
                <div className="mt-2 text-sm font-medium">Drop files here</div>
              </div>
            )}
            {/* Searchbar row */}
            <div className="w-full pl-2 flex items-start">
              <ResourceTagInput
                ref={inputRef}
                autoFocus={autoFocus}
                placeholder={allowFilters ? 'Ask CompliMind...' : 'Ask questions or request edits to this document'}
                suggestion={isRunning ? undefined : followUpSuggestion?.prompt}
                onSuggestionAccepted={() => {
                  if (!followUpSuggestion) return;
                  acceptedFollowUpRef.current = followUpSuggestion;
                  posthog?.capture('chat_follow_up_accepted', {
                    message_id: followUpSuggestion.messageId,
                    session_id: sessionId,
                    follow_up: followUpSuggestion.prompt
                  });
                }}
                disabled={disabled}
                onEnter={disabled || isMicRecording || isRunning ? undefined : formik.handleSubmit}
                onHasContentChange={(hasContent) => {
                  setEditorHasContent(hasContent);
                  // Clearing the input abandons an accepted follow-up.
                  if (!hasContent) acceptedFollowUpRef.current = null;
                }}
                onPastedImages={(files) => {
                  addFiles(files, { filterTypes: false });
                  posthog?.capture('chat_image_pasted', { imageCount: files.length });
                }}
                editorClassName={editorClassName}
              />
              {/* Waveform visualization when recording */}
              {isMicRecording && (
                <div className="flex-1 max-w-xs mx-2 h-8 mt-2">
                  <Waveform audioHistory={audioHistory} isRecording={isMicRecording} className="w-full h-full" />
                </div>
              )}
            </div>

            {fileUpload.uploads.size > 0 && !isWaitingForUploads && (
              <div className="flex flex-wrap items-center gap-2 mt-2 pb-2">
                {Array.from(fileUpload.uploads.entries()).map(([uploadKey, upload]) => {
                  const file = upload.file;
                  const isImage = isImageFile(file);
                  const tokenCount = upload?.tokenCount;
                  const contextPercent =
                    tokenCount != null ? Math.round((tokenCount / GEMINI_CONTEXT_LIMIT) * 100) : null;

                  if (isImage) {
                    return (
                      <div
                        key={uploadKey}
                        className={`relative overflow-hidden flex items-center gap-2 rounded-md text-sm font-medium text-gray-800 p-1 bg-gray-100`}
                      >
                        <ImageItemPill
                          src={getFilePreviewUrl(file)}
                          fallbackSrc={upload.fallbackPreviewUrl}
                          alt={file.name}
                          status={upload.status === 'error' ? undefined : upload.status}
                          uploadProgress={upload.uploadProgress ?? 0}
                          contextPercent={contextPercent}
                          exceedsContextLimit={fileUpload.exceedsContextLimit}
                          onImageClick={() =>
                            setImageModal({
                              open: true,
                              url: getFilePreviewUrl(file),
                              fallbackUrl: upload.fallbackPreviewUrl,
                              filename: file.name,
                              documentId: upload.documentId
                            })
                          }
                          onRemove={() => fileUpload.removeFile(uploadKey)}
                        />
                      </div>
                    );
                  }

                  return (
                    <FileItemPill
                      key={uploadKey}
                      filename={file.name}
                      status={upload.status}
                      uploadProgress={upload.uploadProgress ?? 0}
                      errorMessage={upload.error || 'Upload failed'}
                      contextPercent={contextPercent}
                      exceedsContextLimit={fileUpload.exceedsContextLimit}
                      onRemove={() => fileUpload.removeFile(uploadKey)}
                    />
                  );
                })}
              </div>
            )}
            {/* Context budget warning */}
            {fileUpload.exceedsContextLimit && (
              <div className="text-xs text-red-500 px-1 pb-1">
                Query exceeds CompliMind chat context limit. Please remove some files.
              </div>
            )}

            {(allowFilters || enableFileUpload) && (
              <>
                <input
                  ref={fileInputRef}
                  id="file-upload"
                  type="file"
                  aria-label="Upload files"
                  onChange={(e) => {
                    handleFileChange(e);
                    posthog?.capture('chat_file_uploaded', { fileCount: e.target.files?.length });
                  }}
                  accept={Object.keys(SUPPORTED_TYPES).join(',')}
                  multiple
                  className="hidden"
                  disabled={disabled}
                />
                <input
                  ref={cameraInputRef}
                  id="camera-capture"
                  type="file"
                  onChange={(e) => {
                    handleFileChange(e);
                    posthog?.capture('chat_photo_captured', { fileCount: e.target.files?.length });
                  }}
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  disabled={disabled}
                />
              </>
            )}

            <div className="flex items-center justify-between w-full gap-1 md:gap-2">
              <div className="flex items-center gap-1 md:gap-2 min-w-0 flex-1">
                {(Boolean(allowFilters) || Boolean(enableFileUpload) || enableSpecialist) && (
                  <div className="relative inline-block flex-shrink-0" ref={extrasPlusAnchorRef}>
                    <Button
                      size="md"
                      icon={PlusIcon}
                      variant="outline"
                      onClick={() => {
                        setExtrasMenuOpen(true);
                        setExtrasSubmenu(null);
                      }}
                      aria-label="Add files or specialist"
                      aria-haspopup="menu"
                      aria-expanded={extrasMenuOpen}
                      className="cq-icon-only chat-extras-menu-button"
                      disabled={disabled}
                    />
                  </div>
                )}

                {allowFilters && (
                  <div className="relative inline-block flex-shrink-0" ref={filterButtonAnchorRef}>
                    <Tooltip
                      title={
                        specialistHasFilters
                          ? 'Sources are managed by the selected specialist. Edit the specialist or deselect it to change sources.'
                          : ''
                      }
                    >
                      <span>
                        <Button
                          size="md"
                          icon={FunnelSimpleIcon}
                          text="Sources"
                          variant="outline"
                          onClick={() => {
                            if (!specialistHasFilters) {
                              setFilterMenuOpen(true);
                              setFilterMenuShowFullFilters(false);
                            }
                          }}
                          disabled={disabled || specialistHasFilters}
                          aria-label="Filters"
                          aria-haspopup="menu"
                          aria-expanded={filterMenuOpen}
                          className="cq-icon-only chat-filter-menu-button"
                        />
                      </span>
                    </Tooltip>
                    {filterBadgeCount !== null && !specialistHasFilters && (
                      <span className="absolute -top-1.5 -right-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-secondary-blue text-xs font-medium text-primary-lightblue px-1 lining-nums pointer-events-none">
                        {filterBadgeCount}
                      </span>
                    )}
                  </div>
                )}

                {showInlineSpecialistPill && (
                  <SpecialistPill
                    specialist={selectedSpecialist!}
                    onClick={(e) => openSpecialistPopup(e.currentTarget)}
                    onDismiss={() => handleSelectSpecialist(null)}
                    variant="button"
                    className="cq-icon-only chat-specialist-button min-w-0 max-w-[min(100%,14rem)]"
                  />
                )}

                {showInlineWebSearch && (
                  <div className="inline-flex max-w-60 min-w-0 items-stretch overflow-hidden rounded-lg bg-secondary-blue text-primary-lightblue flex-shrink-0 chat-web-search-button">
                    <Tooltip
                      title={
                        specialistForcesWebSearch
                          ? 'Web Access is enabled by the selected specialist. Edit the specialist or unselect it to turn it off.'
                          : ''
                      }
                    >
                      {/* span wrapper keeps the tooltip working while the
                          button is disabled (MUI Tooltip skips disabled
                          descendants otherwise). */}
                      <span className="min-w-0 flex-1 inline-flex">
                        <button
                          type="button"
                          onClick={handleToggleGoogleSearch}
                          disabled={disabled || isUpdatingGoogleSearch || specialistForcesWebSearch}
                          className="min-w-0 flex-1 inline-flex items-center justify-center gap-1.5 px-2.5 min-h-[36px] text-sm font-normal rounded-l-lg rounded-r-none hover:opacity-90 disabled:opacity-100 md:justify-start border-0 bg-transparent text-inherit"
                          style={{ color: 'inherit' }}
                          aria-label="Web Access On"
                        >
                          {isUpdatingGoogleSearch ? (
                            <SpinnerGapIcon size={18} className="animate-spin shrink-0" />
                          ) : (
                            <GlobeSimpleIcon size={18} weight="light" className="shrink-0" />
                          )}
                          <span className="truncate min-w-0 text-left hidden md:inline">Web Access</span>
                        </button>
                      </span>
                    </Tooltip>
                    {!specialistForcesWebSearch && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          void dismissWebSearch();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="flex-shrink-0 flex items-center justify-center px-1.5 min-h-[36px] hover:opacity-80 focus-visible:outline focus-visible:ring-2 focus-visible:ring-offset-1 border-0 bg-transparent text-inherit rounded-r-lg"
                        style={{ color: 'inherit' }}
                        disabled={disabled}
                        aria-label="Turn off web access"
                        title="Turn off web access"
                      >
                        <XIcon size={16} weight="bold" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1 md:gap-2">
                {/* Microphone Button - changes to tick when recording */}
                <Button
                  size="sm"
                  variant="outline"
                  className="w-[36px] h-[36px] chat-voice-input-button"
                  onClick={async () => {
                    if (isMicProcessing) return; // Disable interaction while processing

                    if (!isMicRecording) {
                      posthog?.capture('chat_voice_input_started');
                      await startRecording();
                    } else {
                      posthog?.capture('chat_voice_input_stopped');
                      await stopRecording();
                    }
                  }}
                  title={isMicRecording ? 'Finish recording' : 'Start voice input'}
                  disabled={disabled}
                >
                  {isMicProcessing ? (
                    <SpinnerGapIcon size={18} className="animate-spin" />
                  ) : isMicRecording ? (
                    <CheckIcon size={18} />
                  ) : (
                    <MicrophoneIcon size={18} />
                  )}
                </Button>

                {/* Send/Cancel Button - changes based on recording state */}
                {isMicRecording ? (
                  // Cancel button when recording
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-[36px] h-[36px]"
                    onClick={async () => {
                      posthog?.capture('chat_voice_input_cancelled');
                      await cancelRecording();
                    }}
                    title="Cancel recording"
                    icon={XIcon}
                  />
                ) : (
                  // Regular send button when not recording and not processing
                  !isMicProcessing && (
                    <Tooltip
                      title={
                        isRunning
                          ? 'Stop'
                          : !isWaitingForUploads && isCanvasScanning
                            ? 'Waiting for images to finish scanning'
                            : !isWaitingForUploads && !filtersReady
                              ? 'Loading search filters…'
                              : !isWaitingForUploads && fileUpload.hasUploadErrors
                                ? 'Remove failed uploads before sending'
                                : !isWaitingForUploads && !(editorHasContent || fileUpload.files.length > 0)
                                  ? 'Write a question or upload a file to submit'
                                  : ''
                      }
                      enterDelay={0}
                      leaveDelay={0}
                    >
                      <span>
                        <Button
                          size="md"
                          onClick={() => {
                            if (isWaitingForUploads) {
                              // Cancel the upload wait and abort in-flight uploads
                              uploadWaitAbortRef.current?.abort();
                            } else if (isRunning) {
                              stopStreaming && stopStreaming();
                            } else {
                              formik.handleSubmit();
                            }
                          }}
                          aria-label={isRunning ? 'Stop' : 'Submit query'}
                          icon={!(isRunning || isWaitingForUploads) ? PaperPlaneRightIcon : SquareIcon}
                          iconProps={{ weight: isRunning || isWaitingForUploads ? 'fill' : 'light' }}
                          disabled={
                            disabled ||
                            (!(isRunning || isWaitingForUploads) &&
                              (isCanvasScanning ||
                                !filtersReady ||
                                fileUpload.hasUploadErrors ||
                                !(editorHasContent || fileUpload.files.length > 0)))
                          }
                        />
                      </span>
                    </Tooltip>
                  )
                )}
              </div>
            </div>
          </div>
          {/* Plus menu: Add files + Specialist */}
          {(Boolean(allowFilters) || Boolean(enableFileUpload) || enableSpecialist) && (
            <Popover
              open={extrasMenuOpen}
              anchorEl={extrasPlusAnchorRef.current}
              onClose={() => {
                setExtrasMenuOpen(false);
                setExtrasSubmenu(null);
              }}
              placement="top-start"
              showBackdrop
              className="!max-w-[min(100vw-1rem,920px)]"
            >
              <div className="flex flex-col md:flex-row text-sm max-w-[min(100vw-1rem,920px)]">
                <div className="py-1 min-w-[240px] shrink-0 md:border-r md:border-border-default">
                  {(allowFilters || enableFileUpload) &&
                    (isFreemiumUser ? (
                      <Tooltip title="Premium feature – click to learn more" placement="right">
                        <div
                          className="w-full cursor-pointer px-1"
                          onClick={() => {
                            setExtrasMenuOpen(false);
                            setExtrasSubmenu(null);
                            handlePremiumFeatureClick(
                              'With full access to CompliMind, you can upload your own documents and images to get AI-powered analysis and answers based on your files.'
                            );
                          }}
                        >
                          <Button
                            icon={PaperclipIcon}
                            variant="ghost"
                            justify="left"
                            fullWidth
                            className="pointer-events-none chat-files-button"
                            disabled
                          >
                            Upload files or photos
                          </Button>
                        </div>
                      </Tooltip>
                    ) : (
                      <Button
                        icon={PaperclipIcon}
                        variant="ghost"
                        justify="left"
                        fullWidth
                        className="chat-files-button"
                        onClick={handleAddFilesFromMenu}
                      >
                        Upload files or photos
                      </Button>
                    ))}

                  <Button icon={AtIcon} variant="ghost" justify="left" fullWidth onClick={handleAddResourceFromMenu}>
                    Add resource as context
                  </Button>

                  {enableSpecialist &&
                    (isFreemiumUser ? (
                      <Tooltip title="Premium feature – click to learn more" placement="right">
                        <div
                          className="w-full cursor-pointer px-1"
                          onClick={() => {
                            setExtrasMenuOpen(false);
                            setExtrasSubmenu(null);
                            handlePremiumFeatureClick(
                              'With full access to CompliMind, you can create and use AI Specialists - customised AI assistants trained for specific tasks and domains.'
                            );
                          }}
                        >
                          <Button
                            icon={UserGearIcon}
                            variant="ghost"
                            justify="left"
                            fullWidth
                            className="pointer-events-none chat-specialist-button"
                            disabled
                          >
                            Specialist
                          </Button>
                        </div>
                      </Tooltip>
                    ) : (
                      <Button
                        variant="ghost"
                        justify="left"
                        fullWidth
                        className={`chat-specialist-button rounded-none [&>span]:w-full ${
                          extrasSubmenu === 'specialist' ? 'bg-surface-hover' : ''
                        }`}
                        onClick={toggleSpecialistSubmenu}
                      >
                        <span className="flex w-full items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-2">
                            <UserGearIcon size={18} weight="light" />
                            Specialist
                          </span>
                          {extrasSubmenu === 'specialist' ? (
                            <CaretLeftIcon size={16} className="text-content-subtler shrink-0" />
                          ) : (
                            <CaretRightIcon size={16} className="text-content-subtler shrink-0" />
                          )}
                        </span>
                      </Button>
                    ))}
                </div>

                {enableSpecialist && !isFreemiumUser && extrasSubmenu === 'specialist' && (
                  <div className="w-full md:min-w-[280px] md:max-w-[340px] max-h-[70vh] overflow-y-auto p-3 border-t md:border-t-0 md:border-l border-border-default">
                    {selectedSpecialist && (
                      <>
                        <p className="text-xs text-content-subtler mb-1.5">Current</p>
                        <button
                          type="button"
                          onClick={() => handleSelectSpecialist(selectedSpecialist)}
                          className="mb-3 w-full text-left"
                        >
                          <SpecialistPill specialist={selectedSpecialist} className="max-w-full" />
                        </button>
                        <div className="border-t border-border-default mb-3" />
                      </>
                    )}
                    <div className="mb-3">
                      <Button
                        onClick={() => {
                          openManageModal?.();
                          setExtrasMenuOpen(false);
                          setExtrasSubmenu(null);
                        }}
                        icon={GearIcon}
                        text="View Specialists"
                        variant="outline"
                        size="md"
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2 overflow-y-auto max-h-[40vh]">
                      {specialistsLoading ? (
                        <div className="text-center py-8 text-content-subtle">Loading specialists...</div>
                      ) : displayedSpecialists.length === 0 ? (
                        <div className="text-center py-6 text-content-subtle text-sm">
                          No specialists created. Use View Specialists to get started.
                        </div>
                      ) : (
                        displayedSpecialists.map((specialist) => (
                          <button
                            key={specialist.id}
                            type="button"
                            onClick={() => handleSelectSpecialist(specialist)}
                            className={`w-full text-left ${
                              selectedSpecialist?.id === specialist.id ? 'rounded-md ring-2 ring-blue-400' : ''
                            }`}
                          >
                            <SpecialistPill specialist={specialist} className="max-w-full" />
                          </button>
                        ))
                      )}
                    </div>
                    {selectedSpecialist && (
                      <div className="mt-3 pt-2 border-t border-border-default">
                        <Button
                          variant="outline"
                          size="md"
                          className="w-full"
                          onClick={() => handleSelectSpecialist(null)}
                          icon={XIcon}
                        >
                          Clear selected specialist
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Popover>
          )}

          {/* Filter menu: web access + category groups + all filters */}
          {allowFilters && (
            <Popover
              open={filterMenuOpen}
              anchorEl={filterButtonAnchorRef.current}
              onClose={() => {
                setFilterMenuOpen(false);
                setFilterMenuShowFullFilters(false);
              }}
              placement="top-start"
              showBackdrop
              className={
                filterMenuShowFullFilters ? '!max-w-[min(100vw-1rem,1040px)]' : '!max-w-[min(100vw-1rem,920px)]'
              }
            >
              <div
                className={`flex flex-col md:flex-row text-sm ${
                  filterMenuShowFullFilters ? 'max-w-[min(100vw-1rem,1040px)]' : 'max-w-[min(100vw-1rem,920px)]'
                }`}
              >
                <div className="w-[240px] min-w-[240px] max-w-[240px] shrink-0 overflow-hidden">
                  <SourcesPanel
                    chatFilters={
                      chatFilters ?? { category: [], domain: [], subDomain: [], document: [], docHubSource: [] }
                    }
                    onCategoryChange={(newCategories) => {
                      if (!setChatFilters || !chatFilters) return;
                      setChatFilters({ ...chatFilters, category: newCategories });
                    }}
                    docHubSources={chatFilters?.docHubSource ?? []}
                    onDocHubSourcesChange={(sources) => {
                      if (!setChatFilters || !chatFilters) return;
                      setChatFilters({ ...chatFilters, docHubSource: sources });
                    }}
                    availableCategories={availableCategoryNames}
                    showWebAccess={isGoogleSearchEnabled}
                    webAccessEnabled={effectiveWebSearchEnabled}
                    onToggleWebAccess={handleToggleGoogleSearch}
                    isUpdatingWebAccess={isUpdatingGoogleSearch}
                    readOnlyWebAccess={specialistForcesWebSearch}
                    webAccessReadOnlyHint="Enabled by the selected specialist"
                    advancedFiltersActiveCount={
                      chatFilters
                        ? chatFilters.domain.length + chatFilters.subDomain.length + chatFilters.document.length
                        : 0
                    }
                    onAllFiltersClick={() => setFilterMenuShowFullFilters((v) => !v)}
                    allFiltersOpen={filterMenuShowFullFilters}
                    wikiHref={toTenantPath('/wiki', activeTenant?.code ?? null)}
                    docHubHref={toTenantPath('/documents', activeTenant?.code ?? null)}
                    onCategoryGroupToggled={(group, enabled) => {
                      posthog?.capture('chat_category_group_toggled', { group: group.key, enabled });
                    }}
                  />
                </div>

                {filterMenuShowFullFilters && (
                  <div className="w-full md:flex-1 md:min-w-0 md:max-w-[780px] h-[min(54rem,80vh)] p-3 overflow-hidden border-t md:border-t-0 md:border-l border-border-default flex flex-col">
                    <AllFiltersPanel
                      chatFilters={
                        chatFilters ?? { category: [], domain: [], subDomain: [], document: [], docHubSource: [] }
                      }
                      onFilterChange={handleFilterSelectionChange}
                      onClearAll={() => {
                        if (!setChatFilters || !chatFilters) return;
                        // Single state update so the three cleared fields aren't
                        // clobbered by stale-closure reads inside handleFilterSelectionChange.
                        // Skip the next auto-deselect pass — it would otherwise
                        // run against stale (pre-clear) aggregations and could
                        // strip valid category selections.
                        skipNextAutoDeselectRef.current = true;
                        setChatFilters({
                          ...chatFilters,
                          domain: [],
                          subDomain: [],
                          document: []
                        });
                        frozenOptionsRef.current = null;
                        posthog?.capture('chat_filter_changed', { filter: 'clear_all', value: [] });
                      }}
                    />
                  </div>
                )}
              </div>
            </Popover>
          )}

          {/* Specialist Popover */}
          <Popover
            open={specialistPopoverOpen}
            anchorEl={specialistPopoverAnchor}
            onClose={closeSpecialistPopup}
            placement="top"
            showBackdrop
          >
            <div className="py-2 px-3 min-w-[200px] max-w-[300px] text-sm">
              <div className="mb-3">
                <Button
                  onClick={() => {
                    openManageModal?.();
                    closeSpecialistPopup();
                    setExtrasMenuOpen(false);
                    setExtrasSubmenu(null);
                  }}
                  icon={GearIcon}
                  text="View Specialists"
                  variant="outline"
                  size="md"
                  className="w-full"
                />
              </div>
              <div className="space-y-2 overflow-y-auto max-h-[50vh]">
                {specialistsLoading ? (
                  <div className="text-center py-8 text-content-subtle">Loading specialists...</div>
                ) : displayedSpecialists.length === 0 ? (
                  <div className="text-center flex py-8 text-content-subtle">
                    No specialists created. Click the button above to get started.
                  </div>
                ) : (
                  <>
                    {displayedSpecialists.map((specialist) => (
                      <button
                        key={specialist.id}
                        onClick={() => handleSelectSpecialist(specialist)}
                        className={`w-full text-left ${
                          selectedSpecialist?.id === specialist.id ? 'rounded-md ring-2 ring-blue-400' : ''
                        }`}
                      >
                        <SpecialistPill specialist={specialist} className="max-w-full" />
                      </button>
                    ))}
                    {selectedSpecialist && (
                      <Button
                        variant="outline"
                        size="md"
                        className="w-full"
                        onClick={() => handleSelectSpecialist(null)}
                        icon={XIcon}
                      >
                        Clear selected specialist
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </Popover>

          {/* Mobile Upload Options Popover */}
          <Menu
            open={mobileUploadPopoverOpen}
            anchorEl={mobileUploadPopoverAnchor}
            onClose={closeMobileUploadPopup}
            placement="top"
          >
            <MenuItem icon={CameraIcon} onClick={handleTakePhoto}>
              Take Photo
            </MenuItem>
            <MenuItem icon={UploadIcon} onClick={handleUploadFile}>
              Upload Photo or File
            </MenuItem>
          </Menu>

          {/* <div className="text-xs text-gray-500 mt-2 mb-1 px-4 pb-1 pt-1">
            CompliMind may display inaccurate info, so double-check its responses and always involve professional
            engineering expertise in decision-making processes.
          </div> */}
        </Form>
      </FormikProvider>

      <ImageModal
        open={imageModal.open}
        url={imageModal.url}
        filename={imageModal.filename}
        documentId={imageModal.documentId}
        fallbackUrl={imageModal.fallbackUrl}
        onClose={() => setImageModal({ open: false, url: '', filename: '' })}
      />

      {/* Premium Upsell Modal for freemium users */}
      <PremiumUpsellModal
        open={premiumModalOpen}
        onClose={() => setPremiumModalOpen(false)}
        featureDescription={premiumModalFeature}
      />
    </div>
  );
}
